/**
 * DAS-2327: Shared tooltip content components for the three return-metric
 * columns (Return Probability, Expected Quarter Return, Reward ÷ Risk).
 *
 * Why this file exists
 * --------------------
 * Before DAS-2327, each of ~5 components (AccountPortfolioModal, FavoritesModal,
 * SelectNowResultsTab, PAModal_Momentum, PAModal_Risk) had hand-copied tooltip
 * JSX for these metrics. They had drifted — some used the full "wind/waves"
 * metaphor, others used one-liners, and regime was shown in only one of three.
 *
 * This module is the single source of truth for tooltip content. Each function
 * returns a JSX fragment you pass as `title={...}` to your Tooltip wrapper
 * (AntD or MUI, caller's choice).
 *
 * Anatomy of each tooltip
 * -----------------------
 * 1. Header: ticker + interpretation (e.g. "+3.76% expected next quarter")
 * 2. Regime + sector context (NEW in DAS-2327 — regime was under-shown before)
 * 3. Formula (compact, not intimidating)
 * 4. Breakdown (concrete numbers for THIS stock)
 * 5. How to read the value (thresholds + colors)
 * 6. Technical caveat (one-liner, points to docs for honest composite-r note)
 * 7. Analysis date (if available)
 *
 * The three metrics in plain English
 * ----------------------------------
 * - Expected Q Return = base_sector_return_Q + fundamental_alpha_Q
 *   (factor-model prediction; alpha = what this stock's fundamentals are worth
 *   relative to sector peers)
 *
 * - Return Probability = Φ(expected_return / residual_vol) × 100
 *   (standard CDF; probability the next-quarter return lands above 0)
 *
 * - Reward ÷ Risk = expected_return / residual_vol
 *   (Information Ratio — expected alpha per unit of unexplained risk)
 *
 * See docs/Expected_Return_Attribution.md for the full derivation and the
 * known composite-r independence-assumption caveat.
 */

import React from 'react';

// ---------------------------------------------------------------------------
// Shared style tokens (kept local to avoid cross-component dependency)
// ---------------------------------------------------------------------------

const S = {
  container: { maxWidth: 340, fontSize: '12px', lineHeight: '1.55' },
  title: { fontWeight: 'bold', marginBottom: 6, fontSize: '13px' },
  sectionBox: {
    marginBottom: 8,
    padding: '6px 8px',
    backgroundColor: 'rgba(255,255,255,0.08)',
    borderRadius: 4,
    fontSize: '11px',
  },
  regimeLine: { fontSize: '10.5px', color: '#bbd', marginBottom: 6 },
  formulaBox: {
    marginBottom: 8,
    padding: '6px 8px',
    backgroundColor: 'rgba(255,255,255,0.12)',
    borderRadius: 4,
    fontSize: '11.5px',
    fontFamily: 'Menlo, monospace',
  },
  caveat: {
    marginTop: 6,
    fontSize: '10px',
    color: '#aab',
    fontStyle: 'italic',
    borderTop: '1px solid #3a3a4a',
    paddingTop: 4,
  },
  date: {
    marginTop: 4,
    fontSize: '10px',
    color: '#999',
  },
};

// ---------------------------------------------------------------------------
// Field extraction — tolerant to different record shapes across call sites
// ---------------------------------------------------------------------------

/**
 * Different components use slightly different record field names. Normalize here.
 *
 * Tolerated shapes:
 *  - Ticker_Performance_Data (AccountPortfolioModal, FavoritesModal, SelectNowResultsTab):
 *      expected_q_return, return_probability, risk_adj_q_return, residual_volatility,
 *      fundamental_alpha_q, base_sector_return_q, return_probability_regime
 *  - fundamental_position_data.return_probability (SearchResultModal, DAS-2327 follow-up):
 *      expected_quarterly_return, probability_positive_return, residual_volatility,
 *      alpha_from_fundamentals_quarterly, base_sector_return_quarterly, regime
 */
const pick = (record) => {
  if (!record) return {};
  const expQ =
    record.expected_q_return ??
    record.expected_quarterly_return ??
    null;
  const residualVol = record.residual_volatility ?? null;
  // Derive riskAdjQ if not explicit — SearchResultModal computes it inline.
  let riskAdjQ = record.risk_adj_q_return ?? null;
  if (riskAdjQ == null && expQ != null && residualVol != null && residualVol > 0) {
    riskAdjQ = Math.round((expQ / residualVol) * 1000) / 1000;
  }
  return {
    ticker: record.Ticker || record.ticker || '',
    expQ,
    returnProb:
      record.return_probability ??
      record.probability_positive_return ??
      null,
    riskAdjQ,
    residualVol,
    alpha:
      record.fundamental_alpha_q ??
      record.alpha_from_fundamentals_quarterly ??
      record.alpha ??
      null,
    baseReturn:
      record.base_sector_return_q ??
      record.base_sector_return_quarterly ??
      record.baseReturn ??
      null,
    quintile: record.composite_quintile ?? null,
    regime: record.return_probability_regime || record.regime || 'BULL',
    sector: record.sector || record.Sector || null,
    consensusDate: record.consensus_updated_at || null,
  };
};

const fmtPct = (v, signed = false) => {
  if (v === null || v === undefined) return 'N/A';
  const prefix = signed && v >= 0 ? '+' : '';
  return `${prefix}${v.toFixed(1)}%`;
};

// DAS-2889: one-year odds per quality group (composite_quintile, 1=best),
// measured on our own price history 2020-2026 (DAS-2877; reconfirms DAS-2330).
const GROUP_ODDS = {
  1: { up: 59, ret: '+7.6%' },
  2: { up: 54, ret: '+4.4%' },
  3: { up: 54, ret: '+4.0%' },
  4: { up: 49, ret: '−1.3%' },
  5: { up: 39, ret: '−17%' },
};

const OneYearOdds = ({ quintile }) => {
  const g = GROUP_ODDS[quintile];
  if (!g) return null;
  return (
    <div style={{ marginBottom: 8 }}>
      <b>Quality group {quintile} of 5</b> (1=best): historically, {g.up} out of
      100 such stocks were higher one year later; the typical result was{' '}
      <b>{g.ret}</b>. Measured on our own data, 2020–2026.
    </div>
  );
};

const RegimeSectorLine = ({ regime, sector }) => (
  <div style={S.regimeLine}>
    Regime: <b>{regime || '?'}</b>
    {sector ? (
      <>
        {' '}· Sector: <b>{sector}</b>
      </>
    ) : null}
  </div>
);

// DAS-2330/2331: empirical fact block shown on every tooltip.
// Numbers come from `validate_live_score.py` over 2016-2025 data, 1.36M
// stock-date rows from Fundamentals_Historical + performance_historical.
// All three tooltip metrics (Exp Q Return, Return Prob, Reward÷Risk) are
// monotonic transforms of the same underlying factor-model score, so "ranked
// by this score" applies identically to all three.
//   - Top 20% of stocks by score: +9.4% median 1-yr return, 61.3% hit rate
//   - Bottom 20%: -13.3% median, 40.6% hit rate
const EmpiricalFact = () => (
  <div
    style={{
      marginTop: 8,
      padding: '6px 8px',
      backgroundColor: 'rgba(24,144,255,0.10)',
      borderRadius: 4,
      fontSize: '10.5px',
      lineHeight: '1.5',
      borderTop: '1px solid #3a4a5a',
    }}
  >
    <b>Does this score actually work?</b>
    <br />
    We ranked every stock by this score each week, then tracked what actually
    happened over the following year — measured twice (2016–2025 and 2020–2026)
    with the same picture:
    <br />• Stocks in the <b>highest-scoring 20%</b>: were higher a year later{' '}
    <b style={{ color: '#52c41a' }}>59% of the time</b> — typical gain <b>+7.6%</b>
    <br />• Stocks in the <b>lowest-scoring 20%</b>: higher only{' '}
    <b style={{ color: '#ff4d4f' }}>39% of the time</b> — typical outcome <b>−17%</b>
    <br />
    <span style={{ color: '#aab' }}>
      To see where this stock's score sits, check the bucket/threshold row
      above. Higher bucket = higher expected outcome.
    </span>
  </div>
);

const AnalysisDate = ({ date }) =>
  date ? <div style={S.date}>Based on analysis from: {date}</div> : null;

// ---------------------------------------------------------------------------
// 1. Expected Q Return tooltip
// ---------------------------------------------------------------------------

export function ExpQReturnTooltip({ record }) {
  const f = pick(record);
  const expQStr = fmtPct(f.expQ, true);
  return (
    <div style={S.container}>
      <div style={S.title} data-testid="one-year-odds-headline">
        {f.ticker}: {f.quintile ? `quality group ${f.quintile} of 5` : `fundamentals score ${expQStr}`}
      </div>
      <RegimeSectorLine regime={f.regime} sector={f.sector} />

      <OneYearOdds quintile={f.quintile} />
      <div style={{ marginBottom: 8 }}>
        <b>What the score means:</b> it ranks this stock against others on the
        strength of its business numbers. Use it to compare stocks — it is not
        a forecast for the coming quarter (at that horizon the score showed no
        predictive power on our own data).
      </div>

      <div style={{ marginBottom: 6 }}>
        <b>Where the ranking comes from:</b>
      </div>
      <div style={S.sectionBox}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <tbody>
            <tr>
              <td>Typical quarter for {f.sector || 'this sector'} in a <b>{f.regime}</b> market:</td>
              <td style={{ textAlign: 'right', color: '#8cf' }}>
                <b>{fmtPct(f.baseReturn, true)}</b>
              </td>
            </tr>
            <tr>
              <td>Bonus from this stock's fundamentals (vs sector peers):</td>
              <td style={{ textAlign: 'right', color: '#8cf' }}>
                <b>{fmtPct(f.alpha, true)}</b>
              </td>
            </tr>
            <tr style={{ borderTop: '1px solid #555' }}>
              <td style={{ paddingTop: 4, fontWeight: 'bold' }}>Combined score (for comparing stocks):</td>
              <td style={{ textAlign: 'right', paddingTop: 4, fontWeight: 'bold', color: '#8cf', fontSize: '13px' }}>
                {expQStr}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div style={{ marginBottom: 6 }}>
        The "bonus from fundamentals" reflects how this stock's ROA, ROE, profit
        margins, etc. rank against its sector peers, weighted by how well each
        of those metrics has historically predicted future returns.
      </div>

      <div style={{ marginBottom: 8, padding: '6px 8px', backgroundColor: 'rgba(82,196,26,0.08)', borderRadius: 4, fontSize: '11.5px' }}>
        <b>What to do with it:</b>
        <br />• Use it to <b>compare stocks</b>: higher score = stronger business numbers vs peers.
        <br />• The proven meaning lives at the <b>one-year</b> horizon (the odds above) — single quarters are dominated by noise.
        <br />• A negative score means weak numbers vs peers, not a prediction that the stock will drop.
      </div>

      <div style={{ marginBottom: 6, fontSize: '11px', color: '#bbb' }}>
        <b>Why some fields show no contribution:</b> fields like P/E only rank
        meaningfully among profitable companies. A money-losing stock with
        P/E = −5 isn't "cheaper than P/E = 5" — it's a different animal. We
        leave those fields blank for it rather than pretending they're bearish.
      </div>

      <EmpiricalFact />
      <AnalysisDate date={f.consensusDate} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// 2. Return Probability tooltip
// ---------------------------------------------------------------------------

export function ReturnProbabilityTooltip({ record }) {
  const f = pick(record);
  const probStr = f.returnProb != null ? `${f.returnProb.toFixed(1)}%` : 'N/A';
  return (
    <div style={S.container}>
      <div style={S.title} data-testid="one-year-odds-headline">
        {f.ticker}: score {probStr}{f.quintile ? ` — quality group ${f.quintile} of 5` : ''}
      </div>
      <RegimeSectorLine regime={f.regime} sector={f.sector} />

      <OneYearOdds quintile={f.quintile} />
      <div style={{ marginBottom: 8 }}>
        <b>What {probStr} means:</b> read it as a ranking, not a quarterly
        probability — higher = stronger fundamentals vs peers. As a literal
        "chance of profit next quarter" this number failed the test on our own
        data: stocks shown above 60% did not beat the rest over the following
        quarter.
      </div>

      <div style={{ marginBottom: 6 }}>
        <b>Why this number:</b> the model expects{' '}
        <b>{fmtPct(f.expQ, true)}</b> on average next quarter, but the stock's
        price can randomly swing roughly <b>±{fmtPct(f.residualVol)}</b> even
        without any fundamental reason. The probability above combines the two:
        a positive expected gain pulls the odds above 50%; a large random swing
        pulls them back toward a coin flip.
      </div>

      <div style={{ marginBottom: 8, padding: '6px 8px', backgroundColor: 'rgba(82,196,26,0.08)', borderRadius: 4, fontSize: '11.5px' }}>
        <b>What to do with it:</b>
        <br />• Compare stocks with it — prefer higher over lower when choosing.
        <br />• The proven odds are <b>one-year</b> odds: best group 59 out of 100 higher, worst group 39 (typical −17%).
        <br />• Do not buy or sell on this number alone for the coming quarter.
      </div>

      <div style={{ marginBottom: 6, fontSize: '11px', color: '#bbb' }}>
        The edge only shows up across several stocks and about a year of
        patience — any single quarter is mostly noise.
      </div>

      <EmpiricalFact />
      <AnalysisDate date={f.consensusDate} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// 3. Reward ÷ Risk tooltip (Information Ratio)
// ---------------------------------------------------------------------------

export function RewardRiskTooltip({ record }) {
  const f = pick(record);
  // DAS-2331: dropped the "Weak / Tiny" labels that read as apology.
  // The empirical fact block below tells the real story — ranking works,
  // buckets here just anchor where THIS value sits in the distribution.
  const interpretation =
    f.riskAdjQ == null
      ? 'N/A'
      : f.riskAdjQ >= 0.5
      ? 'Exceptional'
      : f.riskAdjQ >= 0.3
      ? 'Strong'
      : f.riskAdjQ >= 0.1
      ? 'Typical'
      : f.riskAdjQ >= 0
      ? 'Low'
      : 'Unfavorable';

  return (
    <div style={S.container}>
      <div style={S.title} data-testid="one-year-odds-headline">
        {f.ticker}: {interpretation} signal strength ({f.riskAdjQ != null ? f.riskAdjQ.toFixed(2) : 'N/A'})
      </div>
      <RegimeSectorLine regime={f.regime} sector={f.sector} />

      <div style={{ marginBottom: 8 }}>
        <b>What this number is:</b> the model score divided by the stock's typical random quarterly swing. Higher = the signal stands out more clearly above the noise. Use it to compare stocks — it is not a verified quarterly forecast.
      </div>

      <div style={S.sectionBox}>
        <div style={{ marginBottom: 4 }}>
          <b>Model expected gain (quarterly framing):</b>{' '}
          <span style={{ color: '#8cf' }}>{fmtPct(f.expQ, true)}</span>
        </div>
        <div style={{ marginBottom: 4 }}>
          <b>Typical random swing (up or down):</b>{' '}
          <span style={{ color: '#8cf' }}>±{fmtPct(f.residualVol)}</span>
        </div>
        {f.expQ != null && f.residualVol != null && (
          <div style={{ marginTop: 6, paddingTop: 4, borderTop: '1px solid #555' }}>
            Ratio: {f.expQ.toFixed(1)} ÷ {f.residualVol.toFixed(1)} ={' '}
            <b style={{ fontSize: '13px', color: '#8cf' }}>
              {f.riskAdjQ != null ? f.riskAdjQ.toFixed(2) : '?'}
            </b>
          </div>
        )}
      </div>

      <div style={{ marginBottom: 8, padding: '6px 8px', backgroundColor: 'rgba(82,196,26,0.08)', borderRadius: 4, fontSize: '11.5px' }}>
        <b>Where this value sits:</b>
        <br />• <span style={{ color: '#389e0d' }}><b>&gt; 0.5</b></span> — Exceptional; gain clearly stands out.
        <br />• <span style={{ color: '#52c41a' }}><b>0.3 – 0.5</b></span> — Strong.
        <br />• <span style={{ color: '#fa8c16' }}><b>0.1 – 0.3</b></span> — Typical (most stocks land here).
        <br />• <span style={{ color: '#999' }}><b>0 – 0.1</b></span> — Low; predicted move small vs swings.
        <br />• <span style={{ color: '#ff4d4f' }}><b>&lt; 0</b></span> — Unfavorable; model expects a loss.
      </div>

      <EmpiricalFact />
      <AnalysisDate date={f.consensusDate} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Compact column-header tooltips (used on <span> in TableHead cells)
// ---------------------------------------------------------------------------

export function ExpQReturnHeaderTooltip() {
  return (
    <div style={S.container}>
      <div style={S.title}>Expected Q Return</div>
      <div>
        A relative score: the typical sector return in the current market
        regime, plus a bonus/penalty for how the stock's fundamentals (ROA,
        ROE, margins, etc.) compare to its sector peers. Use it to compare
        stocks — the proven signal lives at the one-year horizon, not the
        quarter.
      </div>
      <div style={{ marginTop: 6, fontSize: '11px', color: '#ccc' }}>
        Hover a cell to see the breakdown for that stock.
      </div>
    </div>
  );
}

export function ReturnProbabilityHeaderTooltip() {
  return (
    <div style={S.container}>
      <div style={S.title}>Return Probability</div>
      <div>
        A fundamentals-strength ranking expressed as a percentage. Higher =
        stronger vs peers. The proven meaning is at one year: the best fifth of
        stocks were higher 59 times out of 100, the worst fifth only 39.
      </div>
      <div style={{ marginTop: 6, fontSize: '11px' }}>
        Not a literal quarterly probability — at the 3-month horizon it showed
        no predictive power on our own data (DAS-2877).
      </div>
      <div style={{ marginTop: 6, fontSize: '11px', color: '#ccc' }}>
        Hover a cell to see why the number is what it is.
      </div>
    </div>
  );
}

export function RewardRiskHeaderTooltip() {
  return (
    <div style={S.container}>
      <div style={S.title}>Reward ÷ Risk</div>
      <div>
        How big is the expected gain compared to the stock's random quarterly
        swings? A measure of <b>signal-to-noise</b>: bigger = the predicted
        move meaningfully stands out above the randomness.
      </div>
      <div style={{ marginTop: 6, fontSize: '11px' }}>
        &gt; 0.5 strong edge · 0.3 – 0.5 decent · 0.1 – 0.3 weak · &lt; 0.1 mostly noise · negative = expected loss
      </div>
      <div style={{ marginTop: 6, fontSize: '11px', color: '#ccc' }}>
        Hover a cell for the per-stock breakdown.
      </div>
    </div>
  );
}

// Exposed for tests
export const _internal = { pick, fmtPct };
